import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Lock, FileSearch, Mail, FilePlus2,
  Building2, Workflow, AlertTriangle, CheckCircle2, Fingerprint,
  History, GitBranch, Briefcase,
} from "lucide-react";
import SEO, { ORG_SCHEMA, breadcrumb, faqSchema } from "../components/SEO";
import StrategicQuestion from "../components/StrategicQuestion";

const FAQ = [
  {
    q: "MetaConnect est-il un outil de détection de fraude ?",
    a: "Non. MetaConnect ne remplace pas vos outils de détection de fraude. Nous renforçons la qualité des flux qui les alimentent en sécurisant les échanges en amont (identification des émetteurs, traçabilité des transmissions, structuration des boîtes MSSanté).",
  },
  {
    q: "Quels types de boîtes MSSanté pouvez-vous mettre en place pour un assureur ?",
    a: "Boîtes organisationnelles rattachées à un service ou une direction (sinistres, expertise médicale, prévention…), boîtes applicatives pour des flux automatisés (échanges avec des éditeurs, partenaires, plateformes de tiers-payant), domaines MSSanté dédiés à votre organisme.",
  },
  {
    q: "Quel est l'apport concret pour un assureur ou une mutuelle ?",
    a: "Réduction des circuits parallèles non maîtrisés (pièces jointes par email classique, fax, papier), traçabilité des échanges entre professionnels et organismes, identification fiable des émetteurs et destinataires, fiabilité documentaire renforcée.",
  },
  {
    q: "Comment se passe la mise en place ?",
    a: "Nous étudions vos circuits documentaires existants, identifions les flux à sécuriser en priorité, déployons les boîtes et domaines MSSanté correspondants et accompagnons vos équipes dans la prise en main. Intégration progressive dans les parcours existants — pas de bascule brutale.",
  },
];

const SCHEMA = [
  ORG_SCHEMA,
  breadcrumb([
    { name: "Accueil", url: "/" },
    { name: "MSSanté pour assureurs et mutuelles", url: "/mssante-assureurs" },
  ]),
  faqSchema(FAQ),
];

export default function MssanteAssureurs() {
  return (
    <div className="bg-[var(--mc-navy-950)]" data-testid="page-mssante-assureurs">
      <SEO
        title="MSSanté pour assureurs et mutuelles · MetaConnect"
        description="Fiabilisez vos échanges de santé avec des circuits MSSanté structurés et traçables : moins de circuits non maîtrisés, acteurs identifiés, documents tracés."
        canonical="/mssante-assureurs"
        schema={SCHEMA}
      />

      {/* HERO */}
      <section className="relative overflow-hidden hex-grid pt-32 pb-20 lg:pt-40 lg:pb-28" data-testid="section-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full"
               style={{ background: "radial-gradient(circle, rgba(45,123,255,0.20), transparent 60%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 glow-pill px-4 py-1.5 rounded-full text-[12px]" data-testid="hero-eyebrow">
              <Briefcase size={13} className="text-[#0369A1]" />
              MSSanté · Assureurs · Mutuelles · Organismes complémentaires
            </div>

            <h1 className="font-display font-semibold text-[#0F2B5B] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] mt-6">
              MSSanté pour{" "}
              <span className="bg-gradient-to-r from-[#7FB3FF] via-[#4F8FFF] to-[#2D7BFF] bg-clip-text text-transparent">
                assureurs et mutuelles
              </span>
            </h1>

            <p className="mt-6 text-[16px] lg:text-[17px] leading-relaxed text-[#475569] max-w-2xl fade-up delay-1">
              <strong className="text-[#0F2B5B]">Sécuriser les échanges de santé, fiabiliser les circuits documentaires
              et réduire les risques liés aux transmissions non tracées.</strong>
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3 fade-up delay-3">
              <Link to="/demo" data-testid="hero-cta-contact"
                    className="btn-primary px-6 py-3 rounded-full text-[14px] font-medium inline-flex items-center gap-2">
                Échanger avec un expert <ArrowRight size={16} />
              </Link>
              <Link to="/conformite" data-testid="hero-cta-conformite"
                    className="btn-ghost px-6 py-3 rounded-full text-[14px] font-medium">
                Voir le cadre de conformité
              </Link>
            </div>

            <div className="mt-12 flex items-center gap-6 text-[12px] text-[#475569] flex-wrap fade-up delay-4">
              <Stat icon={<ShieldCheck size={16} />} label="Espace de Confiance ANS" />
              <Stat icon={<Lock size={16} />} label="HDS · RGPD" />
              <Stat icon={<Fingerprint size={16} />} label="Identification CPS / PSC" />
            </div>
          </div>

          <div className="lg:col-span-5 fade-up delay-2">
            <FlowVisual />
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* STRATEGIC QUESTION (V3) */}
      <StrategicQuestion />

      <div className="section-divider" />

      {/* INTRO (V2 — corporate) */}
      <section className="relative hex-soft py-24 lg:py-32" data-testid="section-intro">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <SectionEyebrow>Le constat</SectionEyebrow>
          <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
            Comment réceptionnez-vous aujourd'hui les{" "}
            <span className="text-[#0369A1]">demandes et pièces justificatives</span>{" "}
            des professionnels de santé&nbsp;?
          </h2>
          <div className="mt-6 grid gap-4 text-[15px] lg:text-[16px] text-[#475569] leading-relaxed">
            <p>Les circuits actuels reposent souvent sur :</p>
            <ul className="grid gap-2 ml-2">
              <li className="flex items-start gap-2.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--mc-blue-400)] mt-2.5 shrink-0" />
                <span>Des canaux multiples (email, plateformes, papier)</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--mc-blue-400)] mt-2.5 shrink-0" />
                <span>Des transmissions non homogènes</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--mc-blue-400)] mt-2.5 shrink-0" />
                <span>Une identification parfois incertaine des émetteurs</span>
              </li>
            </ul>
            <p>
              Dans ce contexte, une question essentielle se pose : vos flux sont-ils{" "}
              <strong className="text-[#0F2B5B]">réellement sécurisés, tracés et conformes à un cadre
              clair et structuré</strong>&nbsp;?
            </p>
            <p>
              L'absence de standardisation des échanges complique les contrôles, fragilise la
              fiabilité des documents, augmente le risque d'anomalies et limite l'exploitation
              des données.
            </p>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* POSITIONING — we don't replace fraud detection */}
      <section className="relative py-24 lg:py-32 hex-grid" data-testid="section-positioning">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="mc-card p-8 lg:p-10 border-emerald-200">
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-emerald-600">
                  <ShieldCheck size={13} /> Notre positionnement
                </div>
                <h2 className="mt-4 font-display text-[#0F2B5B] text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight">
                  Nous ne <span className="text-[#FFB4B4]">remplaçons pas</span> vos outils de
                  détection de fraude
                </h2>
                <p className="mt-4 text-[15px] text-[#0F2B5B]">
                  Nous <strong className="text-emerald-600">renforçons la qualité des flux
                  qui les alimentent.</strong>
                </p>
                <p className="mt-3 text-[14px] text-[#475569] leading-relaxed">
                  MetaConnect intervient comme une brique de sécurisation des échanges, en amont
                  de votre chaîne documentaire. Vos outils métiers (LAD, OCR, scoring, détection
                  de fraude…) reçoivent des documents fiabilisés, identifiés et tracés.
                </p>
              </div>

              <div className="lg:col-span-6 grid gap-3">
                {[
                  { icon: <Fingerprint size={16} />, text: "Identification des émetteurs et destinataires" },
                  { icon: <Mail size={16} />, text: "Transmission via un canal sécurisé" },
                  { icon: <Building2 size={16} />, text: "Structuration des boîtes MSSanté" },
                  { icon: <History size={16} />, text: "Traçabilité des échanges" },
                  { icon: <GitBranch size={16} />, text: "Réduction des circuits non maîtrisés" },
                  { icon: <FileSearch size={16} />, text: "Meilleure fiabilité documentaire" },
                ].map((item) => (
                  <div key={item.text}
                       className="flex items-start gap-3 rounded-lg border border-[rgba(127,179,255,0.14)] bg-[#F8FAFC] p-3.5">
                    <span className="grid place-items-center h-8 w-8 rounded-md bg-[rgba(110,231,197,0.10)] border border-emerald-200 text-emerald-600 shrink-0">
                      {item.icon}
                    </span>
                    <span className="text-[14px] text-[#334155]">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* FRAUD CONDITIONS */}
      <section className="relative py-24 lg:py-32 hex-soft" data-testid="section-fraud">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <SectionEyebrow>Conditions favorisant la fraude</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
              Un <span className="text-[#0369A1]">levier</span> contre les conditions{" "}
              favorisant la fraude
            </h2>
            <p className="mt-5 text-[15px] text-[#475569] leading-relaxed">
              La fraude documentaire est souvent facilitée par des échanges faibles ou non maîtrisés :
              documents transmis hors cadre, absence de preuve d'envoi, difficulté à identifier
              l'émetteur, prescriptions circulant par email classique.
            </p>
            <p className="mt-3 text-[15px] text-[#475569] leading-relaxed">
              La <strong className="text-[#0F2B5B]">MSSanté</strong> permet de renforcer la confiance
              dans les échanges entre professionnels de santé, structures et organismes concernés.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            <RiskCard icon={<AlertTriangle size={20} />} title="Documents hors cadre"
                      desc="Pièces jointes par email classique, sans preuve de transmission ni signature." />
            <RiskCard icon={<FilePlus2 size={20} />} title="Pas de preuve d'envoi"
                      desc="Aucun horodatage opposable ni piste d'audit en cas de litige ou d'enquête." />
            <RiskCard icon={<Fingerprint size={20} />} title="Émetteur non identifié"
                      desc="L'expéditeur peut être usurpé ou non vérifiable, ouvrant la voie à la fraude documentaire." />
            <RiskCard icon={<Mail size={20} />} title="Prescriptions par email"
                      desc="Les ordonnances qui circulent hors MSSanté sont plus facilement falsifiables ou réutilisables." />
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* WHAT METACONNECT BUILDS */}
      <section className="relative py-24 lg:py-32 hex-grid" data-testid="section-solution">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <SectionEyebrow>Notre offre</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
              Ce que MetaConnect peut{" "}
              <span className="text-[#0369A1]">mettre en place</span>
            </h2>
            <p className="mt-5 text-[15px] text-[#475569] leading-relaxed">
              Une architecture MSSanté adaptée à la réalité opérationnelle d'un assureur ou d'une
              mutuelle, conçue pour s'intégrer progressivement dans vos parcours existants.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            <SolutionCard
              icon={<Building2 size={22} />}
              title="Domaines MSSanté dédiés"
              desc="Un sous-domaine reflétant votre identité d'organisme (ex: votremutuelle.mssante.fr) — reconnu dans l'annuaire santé national."
            />
            <SolutionCard
              icon={<Briefcase size={22} />}
              title="Boîtes organisationnelles"
              desc="Des boîtes MSSanté rattachées à un service ou à une direction (sinistres, expertise, prévention…), avec gestion fine des accès."
            />
            <SolutionCard
              icon={<Workflow size={22} />}
              title="Boîtes applicatives"
              desc="Des boîtes dédiées aux flux automatisés (échanges avec des éditeurs, plateformes de tiers-payant, partenaires de gestion)."
            />
            <SolutionCard
              icon={<ShieldCheck size={22} />}
              title="Circuits sécurisés"
              desc="Des circuits MSSanté structurés entre professionnels de santé, structures et organismes concernés."
            />
            <SolutionCard
              icon={<FileSearch size={22} />}
              title="Accompagnement à l'usage"
              desc="Formation des équipes, conduite du changement, documentation des bonnes pratiques métier."
            />
            <SolutionCard
              icon={<GitBranch size={22} />}
              title="Intégration progressive"
              desc="Pas de bascule brutale : nous priorisons les flux à fort enjeu et déployons par paliers, en mesurant l'adoption."
            />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden py-24 lg:py-32 bg-[var(--mc-navy-900)]" data-testid="section-final-cta">
        <div className="absolute inset-0 pointer-events-none opacity-60">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full"
               style={{ background: "radial-gradient(circle, rgba(45,123,255,0.18), transparent 60%)" }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <SectionEyebrow center>Prochaine étape</SectionEyebrow>
          <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
            Vous souhaitez{" "}
            <span className="text-[#0369A1]">sécuriser vos flux de santé</span> ?
          </h2>
          <p className="mt-5 text-[15px] text-[#475569] max-w-3xl mx-auto leading-relaxed">
            MetaConnect vous accompagne dans la mise en place d'une stratégie MSSanté adaptée aux
            assureurs, mutuelles et organismes complémentaires.
          </p>
          <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/demo" data-testid="cta-final-contact"
                  className="btn-primary px-7 py-3.5 rounded-full text-[14px] font-medium inline-flex items-center gap-2">
              Échanger sur votre besoin <ArrowRight size={16} />
            </Link>
            <a href="mailto:contact@metaconnect.fr" data-testid="cta-final-email"
               className="btn-ghost px-7 py-3.5 rounded-full text-[14px] font-medium">
              contact@metaconnect.fr
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 lg:py-32 hex-soft" data-testid="section-faq">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <SectionEyebrow>Questions fréquentes</SectionEyebrow>
          <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight mt-4 mb-10">
            Vous vous demandez peut-être…
          </h2>
          <div className="grid gap-4">
            {FAQ.map((item, idx) => (
              <details key={item.q} className="mc-card p-5 lg:p-6 group" data-testid={`faq-${idx}`}>
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <h3 className="font-display text-[#0F2B5B] text-[16px] lg:text-[17px] leading-snug">
                    {item.q}
                  </h3>
                  <span className="text-[#0369A1] shrink-0 mt-1 transition-transform group-open:rotate-45">
                    <ArrowRight size={16} className="rotate-45" />
                  </span>
                </summary>
                <p className="mt-4 text-[14px] text-[#475569] leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------- Sub components ---------- */
function Stat({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-2 text-[#0F2B5B]">
      {icon}<span>{label}</span>
    </span>
  );
}

function SectionEyebrow({ children, center = false }) {
  return (
    <div className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#0369A1] ${center ? "" : ""}`}>
      <span className="h-px w-7 bg-[var(--mc-blue-400)]" />
      {children}
    </div>
  );
}

function RiskCard({ icon, title, desc }) {
  return (
    <div className="mc-card p-5 lg:p-6">
      <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(255,170,80,0.08)] border border-[rgba(255,170,80,0.30)] text-[#FFC97D]">
        {icon}
      </div>
      <h3 className="mt-5 font-display text-[#0F2B5B] text-[17px] leading-snug">{title}</h3>
      <p className="mt-2.5 text-[13.5px] text-[#475569] leading-relaxed">{desc}</p>
    </div>
  );
}

function SolutionCard({ icon, title, desc }) {
  return (
    <div className="mc-card p-7 lg:p-8">
      <div className="grid place-items-center h-12 w-12 rounded-xl bg-[rgba(45,123,255,0.15)] border border-[rgba(127,179,255,0.28)] text-[#0369A1]">
        {icon}
      </div>
      <h3 className="mt-5 font-display text-[#0F2B5B] text-[19px] leading-snug">{title}</h3>
      <p className="mt-3 text-[14px] text-[#475569] leading-relaxed">{desc}</p>
    </div>
  );
}

function FlowVisual() {
  return (
    <div className="mc-card p-7 lg:p-8 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-40"
           style={{ background: "radial-gradient(circle at 30% 20%, rgba(45,123,255,0.18), transparent 50%)" }} />
      <div className="relative grid gap-4">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#0369A1] flex items-center gap-2">
          <Workflow size={13} /> Flux documentaire sécurisé
        </div>

        <FlowStep n="1" title="Professionnel de santé"
                 sub="Émet un document via sa BAL MSSanté"
                 icon={<Fingerprint size={16} />} tone="blue" />
        <FlowArrow />
        <FlowStep n="2" title="Canal MSSanté"
                 sub="Chiffré · horodaté · annuaire ANS"
                 icon={<ShieldCheck size={16} />} tone="mint" />
        <FlowArrow />
        <FlowStep n="3" title="Boîte de votre organisme"
                 sub="contact@votremutuelle.mssante.fr"
                 icon={<Building2 size={16} />} tone="blue" />
        <FlowArrow />
        <FlowStep n="4" title="Vos outils métiers"
                 sub="LAD · OCR · scoring · détection fraude"
                 icon={<FileSearch size={16} />} tone="dim" />
      </div>
    </div>
  );
}

function FlowStep({ n, title, sub, icon, tone }) {
  const cls = tone === "mint"
    ? "bg-[rgba(110,231,197,0.10)] border-emerald-200 text-emerald-600"
    : tone === "blue"
      ? "bg-[#EFF6FF] border-[#BAE6FD] text-[#0369A1]"
      : "bg-[rgba(127,179,255,0.06)] border-slate-200 text-[#475569]";
  return (
    <div className="flex items-center gap-3 rounded-lg border border-[rgba(127,179,255,0.14)] bg-[#0F2B5B] p-3">
      <span className={`grid place-items-center h-9 w-9 rounded-lg border ${cls}`}>{icon}</span>
      <div className="min-w-0">
        <div className="text-[12.5px] text-[#0F2B5B]">{title}</div>
        <div className="text-[11px] text-[#475569] truncate">{sub}</div>
      </div>
      <span className="ml-auto text-[10px] font-mono text-[#475569]">{n}</span>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="flex justify-center">
      <span className="h-4 w-px bg-[rgba(127,179,255,0.32)]" />
    </div>
  );
}
